import supabase from 'modules/core/supabase/supabase';
import { TEMPLATE_RESOLUTIONS } from 'modules/manager/domain/template-resolutions';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  DEFAULT_TEMPLATE_TYPE_OPTION,
  MediaManagerTemplateFormValues,
} from '../../components/forms/media-manager-template-form';

interface Args {
  tournamentId: number | null;
  existingNames: string[];
  templatesCount: number;
  onCreated: () => void;
}

function useCreateTemplateModal({ tournamentId, existingNames, templatesCount, onCreated }: Args) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      if (!tournamentId) {
        console.error('Cannot create template without an active tournament');
        return;
      }
      setIsLoading(true);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await supabase.mediaManager.createTemplate({
          name: values.name.trim(),
          objects: [],
          version: '5.3.0',
          tournamentId,
          order: templatesCount,
          width: resolution.width,
          height: resolution.height,
          templateType: values.templateType.value,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        });
        onCreated();
        close();
      } catch (error) {
        console.error('Failed to create media manager template:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [tournamentId, templatesCount, onCreated, close],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type="title-3" className="mb-5">
          Создать шаблон
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: '',
            resolution: DEFAULT_RESOLUTION_OPTION,
            templateType: DEFAULT_TEMPLATE_TYPE_OPTION,
            subtype: null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useCreateTemplateModal;
